import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu, X, ChevronRight, ChevronDown } from "lucide-react";
import { COMPANY, NAV } from "../mock";
import { SEO_SERVICES } from "../seo";
import { Button } from "./ui/button";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileServices(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-[900] transition-all duration-300 ${
          scrolled
            ? "bg-brand-navy/95 backdrop-blur-md shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)] py-2"
            : "bg-gradient-to-b from-brand-navydark/90 to-transparent py-3"
        }`}
      >
        <div className="container-x flex items-center justify-between gap-4">
          <Link to="/" aria-label={`${COMPANY.name} home`} className="flex items-center shrink-0">
            <img
              src={COMPANY.logo}
              alt={`${COMPANY.name} logo`}
              className={`logo-glow transition-all duration-300 ${scrolled ? "h-12" : "h-14 md:h-16"}`}
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((item) =>
              item.dropdown ? (
                <div key={item.to} className="relative group">
                  <Link
                    to={item.to}
                    className={`flex items-center gap-1 px-4 py-2 text-sm font-semibold tracking-wide rounded-md transition-colors ${
                      isActive(item.to) ? "text-brand-gold2" : "text-white/90 hover:text-white"
                    }`}
                  >
                    {item.label}
                    <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                  </Link>
                  {/* Mega dropdown */}
                  <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                    <div className="w-[560px] rounded-2xl bg-white shadow-2xl ring-1 ring-brand-mist p-3 grid grid-cols-2 gap-1">
                      <Link
                        to="/services"
                        className="col-span-2 flex items-center justify-between rounded-xl bg-brand-navy text-white px-4 py-3 mb-1 hover:bg-brand-ocean transition-colors"
                      >
                        <span className="font-heading font-bold text-sm">All Services Overview</span>
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                      {SEO_SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={s.path}
                          className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-brand-mist/70 transition-colors group/item"
                        >
                          <img src={s.image} alt="" className="h-11 w-11 rounded-lg object-cover shrink-0" />
                          <span className="text-sm font-heading font-bold text-brand-navy leading-tight group-hover/item:text-brand-ocean transition-colors">
                            {s.nav}
                          </span>
                        </Link>
                      ))}
                      <Link
                        to="/service-areas"
                        className="col-span-2 flex items-center justify-between rounded-xl bg-brand-mist text-brand-navy px-4 py-3 mt-1 hover:bg-brand-sky/20 transition-colors"
                      >
                        <span className="font-heading font-bold text-sm">Service Areas — Brevard County, FL</span>
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`relative px-4 py-2 text-sm font-semibold tracking-wide rounded-md transition-colors ${
                    isActive(item.to) ? "text-brand-gold2" : "text-white/90 hover:text-white"
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-0.5 rounded-full bg-brand-gold transition-transform origin-left ${
                      isActive(item.to) ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </Link>
              )
            )}
          </nav>

          <div className="hidden xl:flex items-center gap-3">
            <a
              href={COMPANY.phoneHref}
              data-call-source="header"
              data-testid="header-call"
              className="flex items-center gap-2 text-white font-heading font-bold hover:text-brand-gold2 transition-colors"
            >
              <span className="grid place-items-center h-9 w-9 rounded-full bg-white/10 ring-1 ring-white/20">
                <Phone className="h-4 w-4" />
              </span>
              <span className="leading-none">
                <span className="block text-[10px] font-body font-medium text-white/60 uppercase tracking-wider">
                  Call Now
                </span>
                {COMPANY.phone}
              </span>
            </a>
            <Button
              onClick={() => navigate("/estimate")}
              className="bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-bold shadow-gold"
            >
              Free Estimate
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="lg:hidden grid place-items-center h-11 w-11 rounded-lg bg-white/10 ring-1 ring-white/20 text-white"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-0 z-[899] transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-brand-navydark/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
        <nav
          className={`absolute right-0 top-0 h-full w-[85%] max-w-sm bg-brand-navy shadow-2xl transition-transform duration-300 flex flex-col ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
          aria-label="Mobile"
        >
          <div className="flex items-center justify-between px-5 h-[76px] border-b border-white/10">
            <img src={COMPANY.logo} alt="" className="h-11 logo-glow" />
            <button onClick={() => setOpen(false)} aria-label="Close menu" className="h-10 w-10 grid place-items-center rounded-lg bg-white/10 text-white">
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-2">
            {NAV.map((item) =>
              item.dropdown ? (
                <div key={item.to} className="border-b border-white/5">
                  <button
                    onClick={() => setMobileServices((v) => !v)}
                    aria-expanded={mobileServices}
                    className={`w-full flex items-center justify-between px-6 py-4 text-lg font-heading font-semibold ${
                      isActive(item.to) ? "text-brand-gold2" : "text-white"
                    }`}
                  >
                    {item.label}
                    <ChevronDown className={`h-5 w-5 transition-transform ${mobileServices ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`grid transition-all duration-300 ${mobileServices ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden bg-brand-navydark/50">
                      <Link to="/services" className="block px-8 py-3 text-sm font-heading font-bold text-brand-sky2">
                        All Services Overview
                      </Link>
                      {SEO_SERVICES.map((s) => (
                        <Link key={s.slug} to={s.path} className="block px-8 py-3 text-sm text-white/85 border-t border-white/5">
                          {s.nav}
                        </Link>
                      ))}
                      <Link to="/service-areas" className="block px-8 py-3 text-sm font-heading font-bold text-brand-gold2 border-t border-white/5">
                        Service Areas
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center justify-between px-6 py-4 text-lg font-heading font-semibold border-b border-white/5 ${
                    isActive(item.to) ? "text-brand-gold2 bg-white/5" : "text-white"
                  }`}
                >
                  {item.label}
                  <ChevronRight className="h-5 w-5 opacity-60" />
                </Link>
              )
            )}
          </div>
          <div className="p-5 space-y-3 border-t border-white/10">
            <Button
              onClick={() => navigate("/estimate")}
              className="w-full h-12 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-bold text-base shadow-gold"
            >
              Get My Free Estimate
            </Button>
            <a
              href={COMPANY.phoneHref}
              data-call-source="header-mobile"
              data-testid="header-mobile-call"
              className="flex items-center justify-center gap-2 w-full h-12 rounded-lg bg-white/10 text-white font-heading font-bold ring-1 ring-white/20"
            >
              <Phone className="h-5 w-5" /> {COMPANY.phone}
            </a>
          </div>
        </nav>
      </div>
    </>
  );
}
